/**
 * /admin/ops/logistics — SCRUM-335: consola operativa de logística BYO.
 * Demos/seed excluidos por defecto, filtros combinables, tabla en vez de
 * lista plana, drill-down por integración.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { clientRequestHeaders } from "@/lib/sm-api";
import { OpsSubnav } from "@/components/OpsSubnav";
import { RefreshCw, Truck } from "lucide-react";
import { BUSINESS_PROFILE_LABELS } from "@/lib/business-profile";

export const Route = createFileRoute("/admin/ops/logistics")({
  component: OpsLogisticsPage,
  head: () => ({ meta: [{ title: "Logística — Operaciones" }] }),
});

interface ProviderRow {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  business_profile: string;
  plan_id: string | null;
  provider: string;
  environment: string;
  active: boolean;
  validation_status: string | null;
  last_sync_at: string | null;
  updated_at: string;
  is_demo: boolean;
  recent_error_count: number;
  last_error_message: string | null;
  last_error_at: string | null;
}

interface ErrorRow {
  id: string;
  tenant_id: string;
  event: string;
  message: string;
  created_at: string;
}

type OpsLogisticsPayload = {
  connectedTenants: number;
  activeIntegrations: number;
  tenantsWithIncidents: number;
  successRatePercent: number | null;
  operationsInPeriod: number;
  byProvider: Array<{ provider: string; count: number }>;
  byEnvironment: { production: number; sandbox: number };
  providers: ProviderRow[];
  recentErrors: ErrorRow[];
};

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
}

function OpsLogisticsPage() {
  const [data, setData] = useState<OpsLogisticsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState("7");
  const [search, setSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");
  const [envFilter, setEnvFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [includeDemos, setIncludeDemos] = useState(false);
  const [detail, setDetail] = useState<ProviderRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/ops/logistics?days=${days}`, { credentials: "include", headers: clientRequestHeaders() });
      const d = (await r.json()) as OpsLogisticsPayload;
      if (r.ok) setData(d);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  const providers = useMemo(() => data?.providers ?? [], [data]);
  const providerNames = useMemo(() => Array.from(new Set(providers.map((p) => p.provider))).sort(), [providers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return providers.filter((p) => {
      if (!includeDemos && p.is_demo) return false;
      if (providerFilter !== "all" && p.provider !== providerFilter) return false;
      if (envFilter !== "all" && p.environment !== envFilter) return false;
      if (statusFilter === "error" && p.recent_error_count === 0) return false;
      if (statusFilter === "ok" && p.recent_error_count > 0) return false;
      if (statusFilter === "inactive" && p.active) return false;
      if (q) {
        const hay = `${p.tenant_name} ${p.tenant_slug} ${p.provider}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [providers, search, providerFilter, envFilter, statusFilter, includeDemos]);

  const detailErrors = useMemo(
    () => (detail ? (data?.recentErrors ?? []).filter((e) => e.tenant_id === detail.tenant_id) : []),
    [detail, data],
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <OpsSubnav hint="Consola operativa de integraciones de envío BYO — demos/seed excluidos por defecto." />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Truck className="h-5 w-5" />
            Logística BYO
          </h1>
          <p className="text-sm text-muted-foreground">Integraciones de envío por tenant, errores y tasa de éxito.</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={days} onChange={(e) => setDays(e.target.value)}>
            <option value="1">24 horas</option>
            <option value="7">7 días</option>
            <option value="30">30 días</option>
          </select>
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { label: "Tenants conectados", value: data?.connectedTenants ?? "—" },
          { label: "Integraciones activas", value: data?.activeIntegrations ?? "—" },
          { label: "Con incidentes", value: data?.tenantsWithIncidents ?? "—" },
          { label: "Tasa de éxito", value: data?.successRatePercent != null ? `${data.successRatePercent}%` : "—" },
          { label: "Operaciones (período)", value: data?.operationsInPeriod ?? "—" },
          { label: "Producción / Sandbox", value: data ? `${data.byEnvironment.production} / ${data.byEnvironment.sandbox}` : "—" },
        ].map((k) => (
          <div key={k.label} className="glass-card p-4">
            <p className="text-xs text-muted-foreground">{k.label}</p>
            <p className="text-2xl font-semibold">{k.value}</p>
          </div>
        ))}
      </div>

      {data?.byProvider && data.byProvider.length > 0 && (
        <div className="glass-card p-4">
          <h2 className="mb-2 text-sm font-semibold">Distribución por proveedor</h2>
          <div className="flex flex-wrap gap-2">
            {data.byProvider.map((p) => (
              <Badge key={p.provider} variant="outline">{p.provider}: {p.count}</Badge>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Input className="h-8 max-w-xs text-xs" placeholder="Buscar tenant, proveedor…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
          <option value="all">Todos los proveedores</option>
          {providerNames.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={envFilter} onChange={(e) => setEnvFilter(e.target.value)}>
          <option value="all">Todos los ambientes</option>
          <option value="production">Production</option>
          <option value="sandbox">Sandbox</option>
        </select>
        <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Todos los estados</option>
          <option value="ok">Sin errores recientes</option>
          <option value="error">Con errores recientes</option>
          <option value="inactive">Inactivas</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={includeDemos} onChange={(e) => setIncludeDemos(e.target.checked)} />
          Incluir demos/seed
        </label>
      </div>

      <section className="glass-card overflow-x-auto p-0">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="p-2">Tenant</th>
              <th className="p-2">Nicho</th>
              <th className="p-2">Plan</th>
              <th className="p-2">Proveedor</th>
              <th className="p-2">Ambiente</th>
              <th className="p-2">Estado</th>
              <th className="p-2">Última sync</th>
              <th className="p-2">Errores ({days}d)</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="p-6 text-center text-muted-foreground">Sin integraciones que coincidan con el filtro.</td>
              </tr>
            ) : (
              filtered.slice(0, 200).map((p) => (
                <tr key={`${p.tenant_id}-${p.provider}`} className="border-t hover:bg-muted/20">
                  <td className="p-2">
                    {p.tenant_name}
                    {p.is_demo ? <span className="ml-1 text-[10px] text-muted-foreground">demo</span> : null}
                  </td>
                  <td className="p-2 text-muted-foreground">{BUSINESS_PROFILE_LABELS[p.business_profile as keyof typeof BUSINESS_PROFILE_LABELS] ?? p.business_profile}</td>
                  <td className="p-2 text-muted-foreground">{p.plan_id ?? "—"}</td>
                  <td className="p-2"><Badge variant="outline">{p.provider}</Badge></td>
                  <td className="p-2">{p.environment}</td>
                  <td className="p-2">
                    {p.active ? <Badge>activo</Badge> : <Badge variant="secondary">inactivo</Badge>}
                  </td>
                  <td className="p-2 text-muted-foreground">{fmtDate(p.last_sync_at)}</td>
                  <td className="p-2">
                    {p.recent_error_count > 0 ? (
                      <span className="font-medium text-amber-600">{p.recent_error_count}</span>
                    ) : (
                      "0"
                    )}
                  </td>
                  <td className="p-2">
                    <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setDetail(p)}>
                      Ver detalle
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      <Dialog open={detail != null} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{detail?.tenant_name} — {detail?.provider}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <p><span className="text-muted-foreground">Ambiente:</span> {detail.environment}</p>
                <p><span className="text-muted-foreground">Estado:</span> {detail.active ? "activo" : "inactivo"}</p>
                <p><span className="text-muted-foreground">Validación:</span> {detail.validation_status ?? "—"}</p>
                <p><span className="text-muted-foreground">Última sync:</span> {fmtDate(detail.last_sync_at)}</p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-muted-foreground">Últimos errores</p>
                {detailErrors.length === 0 ? (
                  <p className="text-xs italic text-muted-foreground">Sin errores en el período seleccionado.</p>
                ) : (
                  <ul className="max-h-48 space-y-1 overflow-y-auto">
                    {detailErrors.map((e) => (
                      <li key={e.id} className="rounded-md border p-2 text-xs">
                        <p className="font-mono text-[11px] text-muted-foreground">{e.event} · {fmtDate(e.created_at)}</p>
                        <p>{e.message}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
